import { Card, CardActionArea, Chip, Grid } from '@mui/material';
import Iconify from '../../iconify';
import { OptionType, QuestionType } from '@/@types/api.types.ts';
import { MatchPlayerStats } from './MatchPlayerStats.tsx';
import { MatchTeamsStats } from './MatchTeamsStats.tsx';
import { MatchTitle } from './MatchTitle.tsx';
import { usePickemStore } from '@/store/usePickem.store.ts';

const getTargetValueText = (
  questionCategory: 'SAVES' | 'GOALS_AGAINTS' | string
) => {
  switch (questionCategory) {
    case 'SAVES':
      return 'SV';
    case 'GOALS_AGAINTS':
      return 'GA';
    default:
      return '';
  }
};

interface OptionPreviewProps {
  option: OptionType & { isSelected?: boolean };
  question: QuestionType;
  lang: string;
  matchStats: any;
  form: any;
}

export const OptionPreview = ({
  option,
  question,
  lang,
  matchStats,
  form,
}: OptionPreviewProps) => {
  const { isScoringMode } = usePickemStore();

  const optionClickHandler = (questionId: number, optionId: number) => {
    if (!isScoringMode) return;

    const questions: QuestionType[] = form.getFieldValue('questions');

    let questionIndex = questions.findIndex(
      (q) => q.question_id === questionId
    );

    if (questionIndex !== -1) {
      let optionIndex = questions[questionIndex].options.findIndex(
        (o) => o.option_id === optionId
      );

      if (optionIndex !== -1) {
        questions[questionIndex].options[optionIndex].is_correct = !(
          questions[questionIndex].options[optionIndex]?.is_correct &&
          questions[questionIndex].options[optionIndex].is_correct
        );

        form.setFieldValue('questions', questions);
      }
    }
  };

  return (
    <>
      {option?.option_image ? (
        <Card
          sx={{
            maxWidth: 110,
            background: '#262626',
            color: '#ffffff',
            position: 'relative',
            border:
              option?.is_correct && option.is_correct
                ? '3px solid #5DB661'
                : 'none',
          }}
        >
          {option?.is_correct && (
            <Iconify
              icon='eva:checkmark-outline'
              sx={{
                transform: 'scale(3)',
                top: -20,
                right: -15,
                position: 'absolute',
                cursor: 'pointer',
                margin: 3,
                color: 'green',
              }}
            />
          )}
          <CardActionArea
            onClick={() =>
              optionClickHandler(question.question_id, option.option_id)
            }
            sx={{ padding: 1 }}
          >
            <Grid
              container
              style={{ textAlign: 'center', padding: 5 }}
              spacing={1}
            >
              {option?.is_correct && option?.isSelected && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    width: 30,
                  }}
                >
                  <img src='/assets/icons/correct-icon.png' alt='correct' />
                </div>
              )}

              {!option.is_correct && option?.isSelected && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    width: 30,
                  }}
                >
                  <img src='/assets/icons/incorrect-icon.png' alt='incorrect' />
                </div>
              )}

              {option.option_secondary_image && (
                <div
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: 10,
                    width: 30,
                  }}
                >
                  <img src={option.option_secondary_image} alt='secondary' />
                </div>
              )}

              {option?.data?.opp_name && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 115,
                    width: 138,
                    background: '#fff',
                    borderTopRightRadius: 20,
                    padding: '5px 5px',
                    textAlign: 'left',
                    color: '#000',
                    fontSize: 10,
                  }}
                >
                  vs {option?.data?.opp_name}
                </div>
              )}

              {option?.data?.jersey_number ? (
                <div
                  style={{ position: 'absolute', left: 10, top: 10, width: 20 }}
                >
                  #{option?.data?.jersey_number}
                </div>
              ) : null}

              <Grid item xs={12}>
                <img src={option.option_image} alt='option' />
              </Grid>
              {option?.opp_name ? (
                <Grid item xs={12}>
                  VS {option.opp_name}
                </Grid>
              ) : null}
              <Grid item xs={12} style={{ fontSize: 12, fontWeight: 'bold' }}>
                {lang ? option[`title_${lang}`] : option.title_en}
              </Grid>
              <Grid
                item
                xs={12}
                style={{ fontSize: 12, fontWeight: 'bold', color: '#16D490' }}
              >
                {lang ? option[`description_${lang}`] : option.description_en}
              </Grid>

              {option.option_sponsor_image && (
                <Grid item xs={12}>
                  <img
                    src={option.option_sponsor_image}
                    style={{ margin: '0 auto' }}
                    alt='sporsor'
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                {option.sponsor_odds}
              </Grid>
              {option?.hot_cold_label_en && (
                <Grid
                  item
                  xs={12}
                  style={{
                    background: '#323232',
                    color: '#fff',
                    borderRadius: 10,
                    padding: 5,
                    marginTop: 10,
                  }}
                >
                  <img
                    src={'/assets/icons/snowflake.png'}
                    style={{ float: 'left' }}
                    alt='snowflake'
                  />

                  {lang
                    ? option[`hot_cold_label_${lang}`]
                    : option.hot_cold_label_en}
                </Grid>
              )}
              {option?.data?.target_score && (
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={6}>
                      {option?.data?.target_score}{' '}
                      {getTargetValueText(question?.category)}
                    </Grid>
                    <Grid item xs={6}></Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </CardActionArea>
        </Card>
      ) : (
        <Chip
          onClick={() =>
            optionClickHandler(question.question_id, option.option_id)
          }
          sx={{
            m: 1,
            border:
              option?.is_correct && option.is_correct
                ? '3px solid #5DB661'
                : 'none',
          }}
          label={lang ? option[`title_${lang}`] : option.title_en}
        />
      )}
      {option?.data?.match_id && matchStats !== null && (
        <Grid container sx={{ mt: 2, maxWidth: 150 }}>
          <Grid item xs={12} sx={{ fontSize: 9 }}>
            <MatchTitle
              matchId={option?.data?.match_id}
              matchStats={matchStats}
            />
          </Grid>
          <Grid item xs={12}>
            {option?.data?.player_id ? (
              <MatchPlayerStats
                matchId={option?.data?.match_id}
                playerId={option?.data?.player_id}
                matchStats={matchStats}
              />
            ) : (
              <MatchTeamsStats
                matchId={option?.data?.match_id}
                matchStats={matchStats}
              />
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};
